import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Field } from "@/components/Bits";

const TYPES = ["Residential Interior", "Commercial Interior", "Experiential", "Set Design",
  "Seasonal Installation", "Event Installation", "Other"];

const budgetNumber = (text) => {
  const m = String(text || "").replace(/,/g, "").match(/(\d+(?:\.\d+)?)\s*k?/i);
  if (!m) return 0;
  const n = parseFloat(m[1]);
  return /k/i.test(m[0]) ? n * 1000 : n;
};

export default function ConvertInquiry({ inquiry, onClose, onDone }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [busy, setBusy] = useState(false);
  const [linkExisting, setLinkExisting] = useState(!!inquiry.client_match);
  const [form, setForm] = useState({
    client_id: inquiry.client_match?.id || "",
    name: inquiry.project_type ? `${inquiry.name} · ${inquiry.project_type}` : inquiry.name,
    project_type: TYPES.includes(inquiry.project_type) ? inquiry.project_type : "Experiential",
    description: inquiry.idea || "",
    address: inquiry.location || "",
    target_completion_date: /^\d{4}-\d{2}-\d{2}$/.test(inquiry.deadline || "") ? inquiry.deadline : "",
    budget_total: budgetNumber(inquiry.budget),
    status: "Lead",
    phase: "Discovery",
    client_name: inquiry.name || "",
    client_email: inquiry.email || "",
    client_phone: inquiry.phone || "",
    client_company: inquiry.company || "",
  });

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, client_id: linkExisting ? form.client_id || null : null,
                        budget_total: Number(form.budget_total) || 0 };
      const { data } = await api.post(`/inquiries/${inquiry.id}/convert`, payload);
      toast.success("Project created from inquiry");
      onDone();
      navigate(`/projects/${data.project.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.project_id) {
        toast.error("This inquiry is already linked to a project");
        onDone();
        navigate(`/projects/${detail.project_id}`);
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex justify-end bg-ink/40 backdrop-blur-sm" onClick={onClose}
      data-testid="convert-inquiry-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-xl overflow-y-auto bg-bone p-7 sm:p-10">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="overline">Convert inquiry</div>
            <h2 className="mt-2 font-serif text-3xl tracking-tight text-ink">New project</h2>
            <p className="mt-3 text-sm leading-relaxed text-stone">
              Everything is prefilled from {inquiry.name}'s inquiry — edit anything before saving.
              No invoice, contract or portal invitation is sent.
            </p>
          </div>
          <button type="button" data-testid="convert-close" onClick={onClose}
            className="text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">Close</button>
        </div>

        <div className="mt-8 space-y-5">
          <div className="card-surface p-5">
            <div className="overline mb-3">Client</div>
            {inquiry.client_match && (
              <label className="mb-4 flex items-start gap-3 text-sm text-ink">
                <input
                  data-testid="convert-link-existing"
                  type="checkbox"
                  checked={linkExisting}
                  onChange={(e) => setLinkExisting(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#BEC221]"
                />
                <span>
                  Link to existing client <strong>{inquiry.client_match.name}</strong>
                  <span className="block text-xs text-fog">
                    Same email as this inquiry — linking avoids a duplicate record.
                  </span>
                </span>
              </label>
            )}
            {linkExisting ? (
              <Field label="Existing client">
                <select data-testid="convert-client-select" value={form.client_id}
                  onChange={set("client_id")} required
                  className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime">
                  <option value="">Choose a client…</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>
                  ))}
                </select>
              </Field>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Client name">
                  <input data-testid="convert-client-name" value={form.client_name} onChange={set("client_name")} required
                    className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
                </Field>
                <Field label="Company">
                  <input data-testid="convert-client-company" value={form.client_company} onChange={set("client_company")}
                    className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
                </Field>
                <Field label="Email">
                  <input data-testid="convert-client-email" value={form.client_email} onChange={set("client_email")}
                    className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
                </Field>
                <Field label="Phone">
                  <input data-testid="convert-client-phone" value={form.client_phone} onChange={set("client_phone")}
                    className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
                </Field>
              </div>
            )}
          </div>

          <Field label="Project name">
            <input data-testid="convert-project-name" value={form.name} onChange={set("name")} required
              className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Project type" hint={inquiry.project_type ? `They said: ${inquiry.project_type}` : null}>
              <select data-testid="convert-project-type" value={form.project_type} onChange={set("project_type")}
                className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select data-testid="convert-project-status" value={form.status} onChange={set("status")}
                className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime">
                {["Lead", "Active", "On Hold"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Estimated budget" hint={inquiry.budget ? `They said: ${inquiry.budget}` : null}>
              <input data-testid="convert-budget" type="number" min="0" step="100" value={form.budget_total}
                onChange={set("budget_total")}
                className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
            </Field>
            <Field label="Target completion" hint={inquiry.deadline ? `They said: ${inquiry.deadline}` : null}>
              <input data-testid="convert-target-date" type="date" value={form.target_completion_date}
                onChange={set("target_completion_date")}
                className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
            </Field>
          </div>

          <Field label="Address / location">
            <input data-testid="convert-address" value={form.address} onChange={set("address")}
              className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
          </Field>

          <Field label="Description">
            <textarea data-testid="convert-description" rows={5} value={form.description} onChange={set("description")}
              className="w-full rounded-sm border border-line bg-white px-4 py-3 text-sm outline-none focus:border-lime" />
          </Field>

          <div className="card-surface p-5" data-testid="convert-kept-details">
            <div className="overline mb-3">Kept on the project</div>
            <p className="text-xs leading-relaxed text-fog">
              Their budget wording, project date, contact details, website notes and inspiration
              uploads are saved to the project as “Original inquiry” — nothing is discarded.
            </p>
            {inquiry.files?.length > 0 && (
              <div className="mt-4 space-y-2">
                {inquiry.files.map((f) => (
                  <a key={f.url} href={f.url} target="_blank" rel="noreferrer"
                    data-testid="convert-inquiry-file"
                    className="block break-all text-xs text-lime-dark hover:underline">
                    {f.original_filename}
                  </a>
                ))}
              </div>
            )}
            <div className="num mt-4 text-xs text-fog">Source inquiry · {inquiry.id}</div>
          </div>
        </div>

        <button
          data-testid="convert-submit"
          type="submit"
          disabled={busy}
          className="mt-8 flex w-full items-center justify-center gap-2 rounded-sm bg-lime px-6 py-3.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
          Create project
        </button>
      </form>
    </div>,
    document.body
  );
}
